// Single source of truth for quiz timing and counts in a reading session.
// Everything else (session list, timed schedule, Quiz Me wrap-around, review
// size) is derived from these values — change them here, not in components.

export const QUIZ_CONFIG = {
  sessionMinutes: 50,
  regularQuizCount: 19,
  regularQuiz: { questions: 1, timeLimitSec: 60, hintAtSecRemaining: 30 },
  finalQuiz: { questions: 3, timeLimitSec: 300, hintAtSecRemaining: 60 },
  intervalSec: 150,      // timed mode: a quiz every 2.5 min; final lands on the last slot
  prepWarningSec: 5,     // "get ready" message shown this long before each quiz
  // Timed mode: on-demand "Extra Quiz" button on top of the schedule. Extras use the
  // regular-quiz settings and never shift the schedule; the button is locked this
  // many seconds before each scheduled quiz. Hidden from students for now; set
  // enabled back to true to bring the button back.
  extraQuiz: { enabled: false, lockBeforeScheduledSec: 20 },
};

export const SESSION_SECONDS = QUIZ_CONFIG.sessionMinutes * 60;
export const FINAL_QUIZ_INDEX = QUIZ_CONFIG.regularQuizCount;
export const TOTAL_QUIZZES = QUIZ_CONFIG.regularQuizCount + 1;
export const TOTAL_SESSION_QUESTIONS =
  QUIZ_CONFIG.regularQuizCount * QUIZ_CONFIG.regularQuiz.questions + QUIZ_CONFIG.finalQuiz.questions;

// Quiz #1..#N followed by the Final Quiz.
export function buildQuizSessions(cfg = QUIZ_CONFIG) {
  const regular = Array.from({ length: cfg.regularQuizCount }, (_, i) => ({
    id: i + 1,
    name: `Quiz #${i + 1}`,
    questions: cfg.regularQuiz.questions,
    timeLimit: cfg.regularQuiz.timeLimitSec,
    hintAtSecRemaining: cfg.regularQuiz.hintAtSecRemaining,
    isFinal: false,
    completed: false,
  }));
  return [...regular, {
    id: cfg.regularQuizCount + 1,
    name: 'Final Quiz',
    questions: cfg.finalQuiz.questions,
    timeLimit: cfg.finalQuiz.timeLimitSec,
    hintAtSecRemaining: cfg.finalQuiz.hintAtSecRemaining,
    isFinal: true,
    completed: false,
  }];
}

// An on-demand extra quiz (1-based number within the session).
export function buildExtraQuizSession(number, cfg = QUIZ_CONFIG) {
  return {
    id: `extra-${number}`,
    name: `Extra Quiz #${number}`,
    questions: cfg.regularQuiz.questions,
    timeLimit: cfg.regularQuiz.timeLimitSec,
    hintAtSecRemaining: cfg.regularQuiz.hintAtSecRemaining,
    isFinal: false,
    isExtra: true,
    completed: false,
  };
}

// sessionStorage key: extra quizzes taken in the latest session (read by the treat review).
export const EXTRA_QUIZ_COUNT_KEY = 'extraQuizzesTaken';

// Timed mode: elapsed-second marks at which each quiz (and its prep message) fires.
export function buildQuizSchedule(cfg = QUIZ_CONFIG) {
  return Array.from({ length: cfg.regularQuizCount + 1 }, (_, i) => ({
    quizIndex: i,
    triggerAt: (i + 1) * cfg.intervalSec,
    prepAt: (i + 1) * cfg.intervalSec - cfg.prepWarningSec,
  }));
}

const lastTrigger = TOTAL_QUIZZES * QUIZ_CONFIG.intervalSec;
if (lastTrigger > SESSION_SECONDS) {
  console.warn(
    `quizConfig: Final Quiz is scheduled at ${lastTrigger}s but the session is only ${SESSION_SECONDS}s — it will never fire in timed mode.`
  );
}
